import { parseFrontmatter, createImageResolver } from './markdown';

export interface CaseStudy {
  slug: string;
  title: string;
  subtitle: string;
  date: string;
  order?: number;
  author: string;
  excerpt: string;
  image?: string;
  tags: string[];
  related?: string[];
  highlights: string[];
  content: string;
  quote?: {
    text: string;
    author: string;
    role: string;
  };
}

// Load all markdown files from content/case-studies at build time
const caseStudyFiles = import.meta.glob('../../content/case-studies/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
});

// Load all images from content/case-studies/assets at build time
const caseStudyImages = import.meta.glob('../../content/case-studies/assets/*.{png,jpg,jpeg,webp,gif,svg}', {
  import: 'default',
  eager: true,
}) as Record<string, string>;

export const getCaseStudyImageUrl = createImageResolver(caseStudyImages);

function parseCaseStudies(): CaseStudy[] {
  const studies: CaseStudy[] = [];

  for (const [path, rawContent] of Object.entries(caseStudyFiles)) {
    // Extract slug from filename
    const filename = path.split('/').pop();
    if (!filename) continue;
    const slug = filename.replace('.md', '');

    // Parse frontmatter and content
    const { data, content } = parseFrontmatter(rawContent as string);

    // Assemble quote from flat frontmatter fields
    const quote = data.quoteText
      ? {
          text: data.quoteText as string,
          author: (data.quoteAuthor as string) || '',
          role: (data.quoteRole as string) || '',
        }
      : undefined;

    studies.push({
      slug,
      title: (data.title as string) || slug,
      subtitle: (data.subtitle as string) || '',
      date: data.date instanceof Date
        ? data.date.toISOString().split('T')[0]
        : String(data.date || ''),
      order: data.order != null ? Number(data.order) : undefined,
      author: (data.author as string) || 'Neumind Team',
      excerpt: (data.excerpt as string) || '',
      image: getCaseStudyImageUrl(data.image as string | undefined),
      tags: Array.isArray(data.tags) ? data.tags : [],
      related: Array.isArray(data.related) ? data.related : undefined,
      highlights: Array.isArray(data.highlights) ? data.highlights : [],
      content,
      quote,
    });
  }

  return studies;
}

export const caseStudies: CaseStudy[] = parseCaseStudies();

export function getCaseStudy(slug: string): CaseStudy | undefined {
  return caseStudies.find((study) => study.slug === slug);
}

export function getCaseStudies(): CaseStudy[] {
  return [...caseStudies].sort((a, b) => {
    if (a.order != null && b.order != null) return a.order - b.order;
    if (a.order != null) return -1;
    if (b.order != null) return 1;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
}

export function getCaseStudiesByTag(tag: string): CaseStudy[] {
  return getCaseStudies().filter((study) =>
    study.tags.some((t) => t.toLowerCase() === tag.toLowerCase())
  );
}
